import * as React from 'react'
import type { ColumnDef } from '@tanstack/react-table'
import { toast } from 'sonner'
import { FlaskConical } from 'lucide-react'

import { DataTableColumnHeader } from '@/components/data-table/data-table-column-header'
import { DataTableCellTimestamp } from '@/components/data-table/data-table-cell'
import { TextWithTooltip } from '@/components/custom/text-with-tooltip'
import { CopyButton } from '@/components/ui/copy-button'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import type { CallRecord } from '../types'
import { CallLabelBadge } from './CallLabelBadge'
import { CallTypeBadge } from './CallTypeBadge'

const UnavailableData = ({
  className = 'text-gray-400',
}: {
  className?: string
}) => <span className={className}>-</span>

/** Column definitions for the Call Logs v2 table. */
export function callsColumns(
  dutyColumnId: 'duty_id' | 'duty_name' = 'duty_name'
): ColumnDef<CallRecord>[] {
  const cols: ColumnDef<CallRecord>[] = [
    {
      accessorKey: 'call_id',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Call ID" />
      ),
      enableHiding: false,
      cell: ({ row }) => {
        const callId = row.getValue('call_id') as string
        return (
          <div className="flex items-center gap-1">
            <TextWithTooltip text={callId} className="max-w-[150px]" />
            <CopyButton
              content={callId}
              label="Copy Call ID"
              onCopy={() => toast.success('Call ID copied to clipboard')}
              onError={() => toast.error('Failed to copy Call ID')}
            />
          </div>
        )
      },
    },
    {
      id: dutyColumnId,
      accessorFn: (row) =>
        dutyColumnId === 'duty_id' ? row.duty_id : row.duty_name,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Duty Name" />
      ),
      cell: ({ row }) => {
        const dutyName = row.original.duty_name
        if (!dutyName) return <UnavailableData />
        return <TextWithTooltip text={dutyName} className="max-w-[250px]" />
      },
    },
    {
      accessorKey: 'created_at',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Date" />
      ),
      // The timerange filter sets a [Date, Date] value; without this, TanStack
      // falls back to string matching and filters out every row.
      filterFn: 'inDateRange',
      cell: ({ row }) => {
        const val = row.getValue('created_at') as string | undefined
        if (!val) return <UnavailableData />
        return <DataTableCellTimestamp date={val} />
      },
    },
    {
      accessorKey: 'patient_name',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Client Name" />
      ),
      cell: ({ row }) => {
        const name = row.getValue('patient_name') as string | null | undefined
        if (!name) return <UnavailableData />
        return <TextWithTooltip text={name} />
      },
    },
    {
      accessorKey: 'phone',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Phone" />
      ),
      cell: ({ row }) => {
        const phone = row.getValue('phone') as number | null | undefined
        if (!phone) return <UnavailableData />
        const phoneStr = phone.toString()
        let formatted = phoneStr
        if (phoneStr.length === 11 && phoneStr.startsWith('1')) {
          formatted = `(${phoneStr.slice(1, 4)}) ${phoneStr.slice(4, 7)}-${phoneStr.slice(7)}`
        } else if (phoneStr.length === 10) {
          formatted = `(${phoneStr.slice(0, 3)}) ${phoneStr.slice(3, 6)}-${phoneStr.slice(6)}`
        }
        return <div className="whitespace-nowrap">{formatted}</div>
      },
    },
    {
      accessorKey: 'type',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Type" />
      ),
      cell: ({ row }) => {
        const type = row.getValue('type') as string | undefined
        const isTest = row.original.test_call
        if (!type && !isTest) return <UnavailableData />
        return (
          <div className="flex items-center gap-1.5">
            {type ? <CallTypeBadge type={type} /> : <UnavailableData />}
            {isTest && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <span
                    role="img"
                    aria-label="Test call"
                    className="text-accent-solid inline-flex">
                    <FlaskConical className="h-4 w-4 shrink-0" aria-hidden />
                  </span>
                </TooltipTrigger>
                <TooltipContent>Test call</TooltipContent>
              </Tooltip>
            )}
          </div>
        )
      },
    },
    {
      accessorKey: 'duration',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Duration (s)" />
      ),
      cell: ({ row }) => {
        const duration = row.getValue('duration') as number | null | undefined
        return (
          <div className="text-center">{duration ?? <UnavailableData />}</div>
        )
      },
    },
    {
      accessorKey: 'ended_reason',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Ended Reason" />
      ),
      cell: ({ row }) => {
        const reason = row.getValue('ended_reason') as string | null | undefined
        if (!reason && row.original.isActivelyProcessing)
          return <Skeleton className="h-4 w-24" />
        if (!reason) return <UnavailableData />
        return <TextWithTooltip text={reason} />
      },
    },
    {
      accessorKey: 'label',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Label" />
      ),
      cell: ({ row }) => {
        const label = row.getValue('label') as string | null | undefined
        const category = row.original.category
        if (!label)
          return row.original.isActivelyProcessing ? (
            <Skeleton className="h-4 w-28" />
          ) : (
            <UnavailableData />
          )
        return <CallLabelBadge label={label} category={category} />
      },
    },
  ]

  return cols
}
